import React from "react";
import moment from "moment";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar as CalendarGrid } from "../../shadcn/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../shadcn/popover";
import { cn } from "../../shadcn/lib/utils";
import "./DateRangeCalendar.css";

const DEFAULT_PRESET_RANGES = [
  { label: "All Time", getValue: () => ({ from: null, to: null }) },
  { label: "Last 7 days", getValue: () => ({ from: moment().subtract(7, "days").toDate(), to: new Date() }) },
  { label: "Last 30 days", getValue: () => ({ from: moment().subtract(30, "days").toDate(), to: new Date() }) },
  { label: "Last month", getValue: () => ({ from: moment().subtract(1, "month").startOf("month").toDate(), to: moment().subtract(1, "month").endOf("month").toDate() }) },
  { label: "Last 3 months", getValue: () => ({ from: moment().subtract(3, "months").startOf("month").toDate(), to: moment().endOf("month").toDate() }) },
  { label: "Last 6 months", getValue: () => ({ from: moment().subtract(6, "months").startOf("month").toDate(), to: moment().endOf("month").toDate() }) },
  { label: "This year", getValue: () => ({ from: moment().startOf("year").toDate(), to: new Date() }) },
  { label: "Last year", getValue: () => ({ from: moment().subtract(1, "year").startOf("year").toDate(), to: moment().subtract(1, "year").endOf("year").toDate() }) },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DateRangeCalendar = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onApply,
  presets,
  buttonClassName,
  placeholder = "Select date range",
  hideCalendarGrid = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempDateRange, setTempDateRange] = React.useState({ from: startDate, to: endDate });
  const [currentMonth, setCurrentMonth] = React.useState(startDate || new Date());
  const [showMonthYearPicker, setShowMonthYearPicker] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState(moment(currentMonth).year());
  const prevIsOpenRef = React.useRef(isOpen);

  const commit = (from, to) => {
    if (onApply) {
      onApply(from || null, to || null);
    } else {
      setStartDate?.(from || null);
      setEndDate?.(to || null);
    }
  };

  const formatDateDisplay = (start, end) => {
    if (!start || !end) return placeholder;
    return `${moment(start).format("MMM DD, YYYY")} ~ ${moment(end).format("MMM DD, YYYY")}`;
  };

  const handleSelect = (range) => {
    if (!range) {
      setTempDateRange({ from: undefined, to: undefined });
      return;
    }
    setTempDateRange({ from: range.from, to: range.to });
  };

  const handleApply = () => {
    commit(tempDateRange.from, tempDateRange.to);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempDateRange({ from: startDate, to: endDate });
    setIsOpen(false);
  };

  const defaultPresets = React.useMemo(
    () =>
      DEFAULT_PRESET_RANGES.map((preset) => {
        const range = preset.getValue();
        const isActive =
          !startDate && !endDate
            ? preset.label === "All Time"
            : !!(
                range.from &&
                range.to &&
                moment(startDate).isSame(range.from, "day") &&
                moment(endDate).isSame(range.to, "day")
              );
        return {
          label: preset.label,
          isActive,
          onSelect: () => commit(range.from, range.to),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startDate, endDate]
  );

  const activePresets = presets || defaultPresets;

  const handlePresetClick = (preset) => {
    preset.onSelect();
    setIsOpen(false);
  };

  const goToPreviousMonth = () => setCurrentMonth(moment(currentMonth).subtract(1, "month").toDate());
  const goToNextMonth = () => setCurrentMonth(moment(currentMonth).add(1, "month").toDate());

  const toggleMonthYearPicker = () => {
    setShowMonthYearPicker((prev) => !prev);
    setSelectedYear(moment(currentMonth).year());
  };

  const handleMonthSelect = (monthIndex) => {
    setCurrentMonth(moment(currentMonth).year(selectedYear).month(monthIndex).toDate());
    setShowMonthYearPicker(false);
  };

  const goToPreviousYear = () => setSelectedYear((year) => year - 1);
  const goToNextYear = () => setSelectedYear((year) => year + 1);

  React.useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setTempDateRange({ from: startDate, to: endDate });
      setCurrentMonth(startDate || new Date());
    }
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, startDate, endDate]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex min-w-[240px] items-center justify-between gap-3 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors hover:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/30",
            buttonClassName
          )}
        >
          <span className={cn("font-medium", startDate && endDate ? "text-gray-900" : "text-gray-500")}>
            {formatDateDisplay(startDate, endDate)}
          </span>
          <CalendarIcon size={16} className="shrink-0 text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" sideOffset={8}>
        <div className="flex overflow-hidden rounded-md">
          <div className="flex min-w-[160px] flex-col gap-1 border-r border-gray-200 bg-gray-50 p-4">
            <div className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Quick Select
            </div>
            {activePresets.map((preset, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  "whitespace-nowrap rounded-md border px-3 py-2 text-left text-sm transition-colors",
                  preset.isActive
                    ? "border-brand-600 bg-brand-50 font-semibold text-brand-700"
                    : "border-transparent text-gray-900 hover:bg-gray-100"
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {hideCalendarGrid ? null : (
          <div className="flex flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={goToPreviousMonth}
                title="Previous Month"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 hover:border-brand-600 hover:text-brand-600"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={toggleMonthYearPicker}
                className="flex-1 rounded-md px-4 py-2 text-center text-[15px] font-semibold hover:bg-gray-100"
              >
                {moment(currentMonth).format("MMMM YYYY")}
              </button>
              <button
                type="button"
                onClick={goToNextMonth}
                title="Next Month"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 hover:border-brand-600 hover:text-brand-600"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {showMonthYearPicker ? (
              <div className="flex min-h-[280px] flex-col gap-5 p-5">
                <div className="flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={goToPreviousYear}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 hover:border-brand-600 hover:text-brand-600"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <div className="min-w-[80px] text-center text-base font-semibold">{selectedYear}</div>
                  <button
                    type="button"
                    onClick={goToNextYear}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 hover:border-brand-600 hover:text-brand-600"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {MONTHS.map((month, index) => {
                    const isCurrentMonth =
                      moment(currentMonth).month() === index && moment(currentMonth).year() === selectedYear;
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleMonthSelect(index)}
                        className={cn(
                          "rounded-md border px-4 py-3 text-sm font-medium transition-colors",
                          isCurrentMonth
                            ? "border-brand-600 bg-brand-600 text-white"
                            : "border-gray-300 hover:border-brand-600 hover:text-brand-600"
                        )}
                      >
                        {month}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="date-range-calendar-wrapper p-3">
                  <CalendarGrid
                    mode="range"
                    selected={tempDateRange}
                    onSelect={handleSelect}
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    numberOfMonths={1}
                    defaultMonth={currentMonth}
                    disabled={{ after: new Date() }}
                    components={{ Nav: () => null, MonthCaption: () => null }}
                  />
                </div>

                <div className="flex gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 rounded-md border border-gray-300 px-5 py-2 text-sm font-medium hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!tempDateRange.from || !tempDateRange.to}
                    className={cn(
                      "flex-1 rounded-md px-5 py-2 text-sm font-semibold transition-colors",
                      !tempDateRange.from || !tempDateRange.to
                        ? "cursor-not-allowed bg-gray-200 text-gray-400"
                        : "bg-brand-600 text-white hover:bg-brand-700"
                    )}
                  >
                    Apply
                  </button>
                </div>
              </>
            )}
          </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangeCalendar;
